import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiImage, EuiLink, EuiText } from '@elastic/eui'

import { RootState } from 'app/rootReducer'
import { AcImageUploader, UploadedImage } from 'components/Basic/AcImageUploader'
import { FormStack } from 'features/builders/agency/FormStack'
import { CreativeLinkMode, InternetChannelDraft, isValidUrl } from 'features/builders/agency/quickCampaignModels'

import { CreativeCard } from './CreativeCard'
import { CreativeLinkSettings, ICreativeLinkSettings } from './CreativeLinkSettings'

export interface IDisplayAdUploader extends Omit<ICreativeLinkSettings, 'links'> {
  draft: InternetChannelDraft
  onImagesUploaded: (images: UploadedImage[]) => void
  onImageRemove: (url: string) => void
  onIsUploadingChange: (isUploading: boolean) => void
  onCreativeLinkChange: (creativeUrl: string, link: string) => void
}

const acceptedFileSize = 300 * 1000000

const openImage = (url: string) => {
  window.open(url, 'sharer', 'toolbar=0,status=0,width=548,height=325')
}

export const DisplayAdUploader: React.FC<IDisplayAdUploader> = ({ idPrefix, draft, showErrors, onImagesUploaded, onImageRemove, onIsUploadingChange, onModeChange, onSharedLinkChange, onCreativeLinkChange }) => {
  const [isValidUpload, setIsValidUpload] = useState<boolean>(true)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const isUnique = draft.links.mode === CreativeLinkMode.Unique

  const linkFor = (image: UploadedImage) => {
    const value = draft.links.linksByCreativeUrl[image.url] ?? ''
    return { value, isInvalid: (showErrors || value.trim().length > 0) && !isValidUrl(value), onChange: (link: string) => onCreativeLinkChange(image.url, link) }
  }

  return (
    <FormStack>
      <CreativeLinkSettings idPrefix={idPrefix} links={draft.links} showErrors={showErrors} onModeChange={onModeChange} onSharedLinkChange={onSharedLinkChange} />
      <EuiFormRow id={`${idPrefix}-uploader`} label='Display Ads' isInvalid={showErrors && draft.uploadedImages.length === 0} error='Upload at least one display ad'>
        <AcImageUploader maxFileSize={acceptedFileSize} onUploadedUrlsChange={onImagesUploaded} onIsValidFileChange={setIsValidUpload} onIsUploadingChange={onIsUploadingChange} accountId={currentAccount!.id} allowMultiple />
      </EuiFormRow>
      {!isValidUpload && (
        <EuiText color='danger' size='s'>
          <p>Some images could not be uploaded. Sizes must be less than {(acceptedFileSize / 1000000).toLocaleString()} MB</p>
        </EuiText>
      )}
      {draft.uploadedImages.length > 0 && (
        <EuiFlexGroup wrap gutterSize='m' responsive={false}>
          {draft.uploadedImages.map(image => {
            const isInvalidSize = draft.invalidImages.some(invalid => invalid.url === image.url)
            return (
              <EuiFlexItem grow={false} key={image.url}>
                <CreativeCard id={`${idPrefix}-${image.url.split('/').pop()}`} fileName={image.fileName} details={`${image.width} x ${image.height}`} warning={isInvalidSize ? 'Invalid dimensions, this ad will not be used' : undefined} link={isUnique && !isInvalidSize ? linkFor(image) : undefined} onRemove={() => onImageRemove(image.url)}>
                  <EuiLink onClick={() => openImage(image.url)}>
                    <EuiImage alt={image.fileName} url={image.url} style={{ maxHeight: 150, maxWidth: '100%', objectFit: 'contain' }} />
                  </EuiLink>
                </CreativeCard>
              </EuiFlexItem>
            )
          })}
        </EuiFlexGroup>
      )}
    </FormStack>
  )
}
